import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Grid,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BusinessIcon from '@mui/icons-material/Business';
import salesService from '../../../../../services/salesService';
import { useAuth, Branch } from '../../../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface AddQuickSaleScreenModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  // Düzenleme modu için props
  editMode?: boolean;
  screenId?: string | number;
  screenName?: string;
  screenBranchId?: string | null;
  onSaved?: () => void; // Kaydetme sonrası callback
}

// Branch interface'i artık AuthContext'ten geliyor

const AddQuickSaleScreenModal: React.FC<AddQuickSaleScreenModalProps> = ({ 
  open, 
  onClose, 
  companyId,
  editMode = false,
  screenId,
  screenName: initialScreenName,
  screenBranchId,
  onSaved
}) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth(); // ✅ AuthContext'ten branches alınıyor
  const [screenName, setScreenName] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false); // Kaydetme durumu için state

  // Modal acilis/kapanis ve duzenleme modu hazirligi (branches AuthContext'ten geliyor)
  useEffect(() => {
    if (!open || !companyId) {
      setScreenName('');
      setSelectedBranch(null);
      setError(null);
      return;
    }

    setError(null);

    // Düzenleme modunda mevcut verileri yükle
    if (editMode) {
      setScreenName(initialScreenName || '');
      // Şube bilgisini yükle
      if (screenBranchId) {
        const branch = branches.find(b => b.id === screenBranchId);
        setSelectedBranch(branch || null);
      } else {
        setSelectedBranch(null);
      }
    } else {
      // Yeni ekran modu - varsayılan değerler
      setScreenName('');
      setSelectedBranch(null);
    }
  }, [open, companyId, editMode, initialScreenName, screenBranchId, branches]);

  const handleSave = async () => {
    if (!screenName || !companyId) {
      setError(t('quickSaleModals.addQuickSale.errors.screenNameRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editMode && screenId) {
        // Güncelleme modu - mevcut ekranı güncelle
        await salesService.updateQuickSaleScreen({
          Id: Number(screenId),
          Name: screenName,
          BranchId: selectedBranch?.id || null,
        });
        console.log("Hızlı Satış Ekranı güncellendi");
      } else {
        // Yeni ekran oluşturma modu
        await salesService.createQuickSaleScreen({
          Name: screenName,
          BranchId: selectedBranch?.id || null,
          // Boş JSON'lar başlangıçta null
          AssignedPersonnelJson: null,
          PaymentOptionsJson: null,
          StockVisibilityJson: null,
          PrintingSettingsJson: null,
          PersonnelPermissionsJson: null,
          // Varsayılan değerler
          PrepaidSystemEnabled: false,
          ShowProductImages: true,
          UseFiscalDeviceForCash: false,
          AllowAccountCreationOnSale: false
        });
        console.log("Yeni Hızlı Satış Ekranı SQL'e kaydedildi");
      }

      // Kaydetme sonrası callback'i çağır
      if (onSaved) {
        onSaved();
      }
      handleClose();

    } catch (err: any) {
      console.error("Hızlı satış ekranı kaydedilirken hata:", err);
      setError(err.response?.data?.Message || t('quickSaleModals.addQuickSale.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Modal kapatılırken state'leri sıfırla
  const handleClose = () => {
    setScreenName('');
    setSelectedBranch(null);
    setError(null);
    setSaving(false);
    onClose(); // Prop'tan gelen kapatma fonksiyonunu çağır
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {editMode ? t('quickSaleModals.addQuickSale.titleEdit') : t('quickSaleModals.addQuickSale.titleCreate')}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Hata Mesajı Alanı */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)} // Hata mesajını kapatma butonu
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Ekran Adı */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('quickSaleModals.addQuickSale.fields.screenNameLabel')}
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              required
              InputProps={{
                startAdornment: <PointOfSaleIcon color="action" sx={{ mr: 1 }} />
              }}
              helperText={t('quickSaleModals.addQuickSale.fields.screenNameHelper')}
            />
          </Grid>

          {/* Atanan Şube */}
          <Grid item xs={12}>
            <Autocomplete
              options={branches.filter(b => b.id !== "null")}
              getOptionLabel={(option) => option.name}
              value={selectedBranch}
              onChange={(_, newValue) => setSelectedBranch(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('quickSaleModals.addQuickSale.fields.assignedBranchLabel')}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {t('quickSaleModals.common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!screenName || saving} // Gerekli alanlar kontrolü
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving
            ? t('quickSaleModals.common.saving')
            : (editMode ? t('quickSaleModals.common.update') : t('quickSaleModals.common.create'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddQuickSaleScreenModal; 